<template>
  <div class="staff-change-risk-addon">
    <div class="addon-head">
      <div>
        <span>HRM_STAFF_CHANGE_RISK</span>
        <h2>人员异动风险洞察</h2>
        <p>聚合员工状态、部门分布、资料缺口和角色覆盖，识别人员异动、主数据缺口与治理风险。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门人员风险</div>
          <el-table :data="overview.deptStaffRisk" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="staff_count" label="人数" width="80" />
            <el-table-column prop="abnormal_count" label="异常" width="80" />
            <el-table-column prop="profile_gap_count" label="资料缺口" width="90" />
            <el-table-column prop="recent_change_count" label="近期变动" width="90" />
            <el-table-column prop="abnormal_rate" label="异常率" width="90">
              <template slot-scope="{ row }">{{ row.abnormal_rate || 0 }}%</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">员工状态分布</div>
          <div class="status-list">
            <div v-for="item in overview.statusDistribution" :key="item.status" class="status-item">
              <span>{{ formatStatus(item.status) }}</span>
              <strong>{{ item.staff_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">资料缺口排行</div>
          <el-table :data="overview.profileGapRanking" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="missing_phone" label="电话" width="70" :formatter="formatFlag" />
            <el-table-column prop="missing_birthday" label="生日" width="70" :formatter="formatFlag" />
            <el-table-column prop="missing_address" label="地址" width="70" :formatter="formatFlag" />
            <el-table-column prop="missing_avatar" label="头像" width="70" :formatter="formatFlag" />
            <el-table-column prop="gap_count" label="缺口数" width="80" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">最近人员变动</div>
          <div class="change-list">
            <div v-for="item in overview.recentChangeList" :key="item.staff_id" class="change-item">
              <strong>{{ item.staff_name }}</strong>
              <span>{{ item.dept_name }} · {{ formatStatus(item.status) }}</span>
              <em>{{ item.update_time || item.create_time || '-' }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="lower-row" shadow="never">
      <div slot="header">角色覆盖缺口</div>
      <el-table :data="overview.roleCoverageGap" size="small" v-loading="loading">
        <el-table-column prop="staff_code" label="员工编码" width="120" />
        <el-table-column prop="staff_name" label="员工" width="120" />
        <el-table-column prop="dept_name" label="部门" min-width="160" />
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">{{ formatStatus(row.status) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getStaffChangeRiskOverview } from '../api/staffChangeRisk'

export default {
  name: 'StaffChangeRiskOverview',
  data () {
    return {
      loading: false,
      overview: {
        staffTotal: 0,
        activeStaff: 0,
        abnormalStaff: 0,
        recentUpdatedStaff: 0,
        missingDeptStaff: 0,
        missingProfileStaff: 0,
        roleMissingStaff: 0,
        deptStaffRisk: [],
        profileGapRanking: [],
        statusDistribution: [],
        recentChangeList: [],
        roleCoverageGap: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '员工总数', value: this.overview.staffTotal, meta: `${this.overview.activeStaff || 0} 名正常` },
        { label: '异常状态', value: this.overview.abnormalStaff, meta: '冻结 / 异常员工', tone: 'red' },
        { label: '资料缺口', value: this.overview.missingProfileStaff, meta: `${this.overview.missingDeptStaff || 0} 人未分配部门`, tone: 'orange' },
        { label: '角色缺口', value: this.overview.roleMissingStaff, meta: `${this.overview.recentUpdatedStaff || 0} 人近期变动`, tone: 'blue' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getStaffChangeRiskOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '人员异动风险洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatStatus (status) {
      return Number(status) === 1 ? '正常' : '异常'
    },
    formatFlag (row, column, value) {
      return Number(value) === 1 ? '缺失' : '完整'
    }
  }
}
</script>

<style lang="less" scoped>
.staff-change-risk-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #266f59;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.change-list {
  display: grid;
  gap: 10px;
}

.status-item,
.change-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.status-item span,
.change-item span,
.change-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.change-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
